import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { lerp, easeInOut } from '../../lab/shared/PouringCylinder';

/**
 * Step-specific animations for Activity 3.18. Coordinates are in world
 * space (the parent group of these animations is at the origin). All
 * the boiling tubes / apparatus pieces in the scene are positioned in
 * world space too, so the animation endpoints line up directly.
 *
 * Layout reference (all world coords):
 *   Bench top:        y = -0.585
 *   Heating tube (A1) base: x = -0.6, the tube rises to y ≈ 0.6 (in holder, tilted)
 *   Limewater tube (A2):    x = +0.7, clamped vertical
 *   Part B tube 1 (HCl):    x = -0.55, clamped vertical
 *   Part B tube 2 (NH3):    x = +0.55, clamped vertical
 *   Part C tube 1 (Pb):     x = -0.45, clamped vertical
 *   Part C tube 2 (KI):     x = +0.45, clamped vertical
 */

interface BaseProps {
  active: boolean;
  progress: number;
}

// =====================================================
// Part A — explicit, clearly-visible Bunsen flame
//
// The shared BunsenBurner's built-in flame uses tiny blue cones with additive
// blending and renders almost invisibly against the dark scene background.
// This component sits ON TOP of the Bunsen burner barrel and produces a much
// more recognisable orange-blue flame with a point light, so students can
// clearly see when heating is active.
// =====================================================
interface PartAFlameProps {
  /** World position of the burner's flame anchor (top of the chimney). */
  position: [number, number, number];
  active: boolean;
  intensity?: number;
}

export function PartAFlame({ position, active, intensity = 1.0 }: PartAFlameProps) {
  const innerRef = useRef<THREE.Mesh>(null);
  const midRef = useRef<THREE.Mesh>(null);
  const outerRef = useRef<THREE.Mesh>(null);
  const lightRef = useRef<THREE.PointLight>(null);

  useFrame((s) => {
    if (!active) return;
    const t = s.clock.elapsedTime * 9;
    const flicker = Math.sin(t) * 0.08 + Math.sin(t * 2.3) * 0.06;
    const base = intensity;
    if (innerRef.current) {
      innerRef.current.scale.set(base * 0.9 + flicker * 0.1, base + flicker, base * 0.9 + flicker * 0.1);
    }
    if (midRef.current) {
      midRef.current.scale.set(base + flicker * 0.12, base * 1.15 + flicker, base + flicker * 0.12);
    }
    if (outerRef.current) {
      outerRef.current.scale.set(base * 1.2 + flicker * 0.18, base * 1.3 + flicker * 0.9, base * 1.2 + flicker * 0.18);
    }
    if (lightRef.current) {
      lightRef.current.intensity = (1.4 + flicker * 0.5) * intensity;
    }
  });

  if (!active) return null;

  return (
    <group position={position}>
      {/* Inner blue cone — the hottest core */}
      <mesh ref={innerRef} position={[0, 0.04, 0]}>
        <coneGeometry args={[0.015, 0.08, 16]} />
        <meshBasicMaterial
          color="#5dd0ff"
          transparent
          opacity={0.92}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {/* Mid yellow-white envelope */}
      <mesh ref={midRef} position={[0, 0.075, 0]}>
        <coneGeometry args={[0.025, 0.13, 20]} />
        <meshBasicMaterial
          color="#fff3a0"
          transparent
          opacity={0.85}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {/* Outer warm orange envelope — the most visible layer */}
      <mesh ref={outerRef} position={[0, 0.10, 0]}>
        <coneGeometry args={[0.040, 0.20, 24]} />
        <meshBasicMaterial
          color="#ff8833"
          transparent
          opacity={0.55}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {/* Bright glow halo */}
      <mesh position={[0, 0.08, 0]}>
        <sphereGeometry args={[0.07, 16, 16]} />
        <meshBasicMaterial
          color="#ff6622"
          transparent
          opacity={0.25}
          blending={THREE.AdditiveBlending}
          depthWrite={false}
        />
      </mesh>
      {/* Point light so the flame actually lights up nearby surfaces */}
      <pointLight
        ref={lightRef}
        position={[0, 0.08, 0]}
        color="#ffbb66"
        distance={1.2}
        decay={2}
        intensity={1.4}
      />
    </group>
  );
}

// =====================================================
// Part A — spatula scoops CuCO3 powder into the heated tube
// =====================================================
const CUCO3_BOTTLE_POS: [number, number, number] = [-1.2, -0.4, 0.45];
const CUCO3_TUBE_MOUTH_POS: [number, number, number] = [-0.40, 0.30, 0];

export function CuCO3PourAnimation({ active, progress }: BaseProps) {
  if (!active) return null;
  const t = easeInOut(progress);
  const x = lerp(CUCO3_BOTTLE_POS[0], CUCO3_TUBE_MOUTH_POS[0] - 0.04, t);
  const y = lerp(CUCO3_BOTTLE_POS[1] + 0.08, CUCO3_TUBE_MOUTH_POS[1] + 0.04, t);
  const z = lerp(CUCO3_BOTTLE_POS[2], CUCO3_TUBE_MOUTH_POS[2], t);
  const tilt = Math.max(0, (progress - 0.55) / 0.45) * 1.0;
  const remaining = Math.max(0, 1 - (progress - 0.55) / 0.45);
  if (progress > 0.93) return null;

  return (
    <group>
      <group position={[x, y, z]} rotation={[0, 0, -tilt]}>
        <mesh castShadow>
          <boxGeometry args={[0.08, 0.004, 0.014]} />
          <meshStandardMaterial color="#9c7240" roughness={0.85} />
        </mesh>
        {/* Wooden handle nub */}
        <mesh position={[-0.045, 0, 0]}>
          <cylinderGeometry args={[0.006, 0.006, 0.02, 10]} rotation-z={Math.PI / 2} />
          <meshStandardMaterial color="#7a5530" roughness={0.85} />
        </mesh>
        {/* Powder pile on the spatula */}
        {remaining > 0.02 && (
          <mesh position={[0.018, 0.003 + 0.005 * remaining, 0]}>
            <coneGeometry args={[0.012 * Math.sqrt(remaining), 0.008 * remaining, 14]} />
            <meshStandardMaterial color="#3fa563" roughness={0.95} />
          </mesh>
        )}
      </group>
      {/* Small green powder cloud falling */}
      {progress > 0.6 && progress < 0.9 && (
        <mesh position={[x + 0.02, y - 0.04, z]}>
          <sphereGeometry args={[0.006, 8, 8]} />
          <meshStandardMaterial color="#3fa563" roughness={0.95} transparent opacity={0.7} />
        </mesh>
      )}
    </group>
  );
}

// =====================================================
// Part A — build apparatus (stopper + delivery tube animate into place)
// =====================================================
export function PartABuildAnimation({ active, progress }: BaseProps) {
  if (!active) return null;
  const stopperT = easeInOut(Math.min(1, progress / 0.5));
  const tubeT = easeInOut(Math.max(0, (progress - 0.4) / 0.6));

  // Stopper descends onto tube #1 mouth (which is around [-0.40, 0.32, 0])
  const stopperY = lerp(0.55, 0.34, stopperT);

  // Delivery tube: draw progress
  return (
    <group>
      <mesh position={[-0.40, stopperY, 0]}>
        <cylinderGeometry args={[0.022, 0.026, 0.04, 18]} />
        <meshStandardMaterial color="#2a1a0a" roughness={0.9} />
      </mesh>
      {/* The actual rubber tube is rendered by the scene's RubberTube component;
          we use tubeT to drive its `progress` prop via the store. */}
      <group userData={{ tubeT }} />
    </group>
  );
}

// =====================================================
// Part B — dropper drips reactant into a test tube
// =====================================================
const HCL_BOTTLE_POS: [number, number, number] = [-1.2, -0.35, 0.6];
const NH3_BOTTLE_POS: [number, number, number] = [1.2, -0.35, 0.6];
// Mouths of the REST-position Part B tubes (centres at ±0.62, vertical, length 0.85 → mouth = centre + 0.425 in Y).
const PART_B_TUBE1_MOUTH: [number, number, number] = [-0.62, 0.78, 0];
const PART_B_TUBE2_MOUTH: [number, number, number] = [ 0.62, 0.78, 0];

function Dropper({ position, rotation, color }: {
  position: [number, number, number];
  rotation: [number, number, number];
  color: string;
}) {
  return (
    <group position={position} rotation={rotation}>
      {/* Glass bulb */}
      <mesh castShadow>
        <sphereGeometry args={[0.014, 14, 14]} />
        <meshPhysicalMaterial color="#dfeefb" transparent opacity={0.5} transmission={0.85} roughness={0.05} />
      </mesh>
      {/* Rubber bulb top */}
      <mesh position={[0, 0.014, 0]} castShadow>
        <sphereGeometry args={[0.012, 12, 12]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.85} />
      </mesh>
      {/* Glass tip */}
      <mesh position={[0, -0.022, 0]}>
        <cylinderGeometry args={[0.0025, 0.0035, 0.03, 10]} />
        <meshPhysicalMaterial color="#dfeefb" transparent opacity={0.55} transmission={0.85} roughness={0.05} />
      </mesh>
      {/* Reactant droplet visible at the tip */}
      <mesh position={[0, -0.038, 0]}>
        <sphereGeometry args={[0.0028, 8, 8]} />
        <meshPhysicalMaterial color={color} transparent opacity={0.85} transmission={0.7} roughness={0.05} />
      </mesh>
    </group>
  );
}

export function DropperAnimation({ active, progress, reactant }: BaseProps & { reactant: 'HCl' | 'NH3' }) {
  if (!active) return null;
  const t = easeInOut(progress);
  const start = reactant === 'HCl' ? HCL_BOTTLE_POS : NH3_BOTTLE_POS;
  const end = reactant === 'HCl' ? PART_B_TUBE1_MOUTH : PART_B_TUBE2_MOUTH;
  const x = lerp(start[0], end[0], t);
  const z = lerp(start[2], end[2], t);
  const arc = Math.sin(t * Math.PI) * 0.18;
  const y = lerp(start[1], end[1] + 0.06, t) + arc;
  const color = reactant === 'HCl' ? '#dceaff' : '#e8f6e0';

  return (
    <group>
      <Dropper position={[x, y, z]} rotation={[0, 0, t * 0.18]} color={color} />
      {/* Drops falling into the tube during the 0.55..0.95 window */}
      {progress > 0.55 && progress < 0.95 && (
        <>
          {[0, 0.03, 0.06].map((dy, i) => {
            const dropT = ((progress - 0.55) / 0.4 + i * 0.18) % 1;
            const dropY = lerp(y - 0.035, end[1] - 0.18, dropT);
            return (
              <mesh key={i} position={[x, dropY, z]}>
                <sphereGeometry args={[0.0024, 8, 8]} />
                <meshPhysicalMaterial color={color} transparent opacity={0.85} transmission={0.7} />
              </mesh>
            );
          })}
        </>
      )}
    </group>
  );
}

// =====================================================
// Part B — bring tubes together (tubes lerp toward centre AND tilt smoothly)
// =====================================================
// Rest positions = tubes held vertically by the clamps on either side of the bench.
// Together positions = tubes tilted ~60° toward the centre so their mouths face
// each other with a small gap between them.
//
// The BoilingTube has total length 0.85 (centred at its position), so each tube
// extends 0.425 from its centre. Centres at ±0.40 with 60° tilt means the mouth
// of the left tube ends at world x ≈ -0.40 + sin(60°)·0.425 ≈ -0.032 and the
// right tube's mouth at +0.032 — a clean ~0.06 gap for smoke to be visible.
const PART_B_REST_LEFT:  [number, number, number] = [-0.62, 0.355, 0];
const PART_B_REST_RIGHT: [number, number, number] = [ 0.62, 0.355, 0];
const PART_B_TOGETHER_LEFT:  [number, number, number] = [-0.40, 0.40, 0];
const PART_B_TOGETHER_RIGHT: [number, number, number] = [ 0.40, 0.40, 0];
const PART_B_TILT = Math.PI / 3; // 60° — looks like a hand holding the tube

export function usePartBTubePositions(
  isBringingTogether: boolean,
  bringProgress: number,
  tubesAreTouching: boolean,
): {
  left: [number, number, number];
  right: [number, number, number];
  leftRotZ: number;
  rightRotZ: number;
  /** Smoke cloud should sit at this world position (midpoint between mouths). */
  smokeCenter: [number, number, number];
} {
  let p = 0;
  if (isBringingTogether) p = bringProgress;
  else if (tubesAreTouching) p = 1;
  const t = easeInOut(p);

  const left: [number, number, number] = [
    lerp(PART_B_REST_LEFT[0],  PART_B_TOGETHER_LEFT[0],  t),
    lerp(PART_B_REST_LEFT[1],  PART_B_TOGETHER_LEFT[1],  t),
    lerp(PART_B_REST_LEFT[2],  PART_B_TOGETHER_LEFT[2],  t),
  ];
  const right: [number, number, number] = [
    lerp(PART_B_REST_RIGHT[0], PART_B_TOGETHER_RIGHT[0], t),
    lerp(PART_B_REST_RIGHT[1], PART_B_TOGETHER_RIGHT[1], t),
    lerp(PART_B_REST_RIGHT[2], PART_B_TOGETHER_RIGHT[2], t),
  ];
  // Rotation around Z transforms the BoilingTube's local +Y direction (the
  // open mouth) according to: x' = -0.425 sin(θ), y' = 0.425 cos(θ).
  // We want the LEFT tube's mouth to swing to +X (toward the centre) and the
  // RIGHT tube's mouth to swing to -X. That means θ_left must be NEGATIVE and
  // θ_right POSITIVE — the opposite of what intuition suggests.
  const leftRotZ  = lerp(0, -PART_B_TILT, t);
  const rightRotZ = lerp(0,  PART_B_TILT, t);

  // Mouth world positions — note the MINUS on sin(θ) because of the rotation
  // matrix above.
  const leftMouthX  = left[0]  - Math.sin(leftRotZ)  * 0.425;
  const leftMouthY  = left[1]  + Math.cos(leftRotZ)  * 0.425;
  const rightMouthX = right[0] - Math.sin(rightRotZ) * 0.425;
  const rightMouthY = right[1] + Math.cos(rightRotZ) * 0.425;
  const smokeCenter: [number, number, number] = [
    (leftMouthX + rightMouthX) / 2,
    (leftMouthY + rightMouthY) / 2,
    0,
  ];

  return { left, right, leftRotZ, rightRotZ, smokeCenter };
}

// =====================================================
// Part C — measuring cylinder pours reactant into a test tube
// =====================================================
const PB_BOTTLE_POS: [number, number, number] = [-1.1, -0.35, 0.55];
const KI_BOTTLE_POS: [number, number, number] = [1.1, -0.35, 0.55];
const PART_C_TUBE1_MOUTH: [number, number, number] = [-0.45, 0.32, 0];
const PART_C_TUBE2_MOUTH: [number, number, number] = [0.45, 0.32, 0];

export function ReagentPourAnimation({
  active,
  progress,
  reactant,
}: BaseProps & { reactant: 'PbNO3' | 'KI' }) {
  if (!active) return null;
  const t = easeInOut(progress);
  const start = reactant === 'PbNO3' ? PB_BOTTLE_POS : KI_BOTTLE_POS;
  const end = reactant === 'PbNO3' ? PART_C_TUBE1_MOUTH : PART_C_TUBE2_MOUTH;
  const x = lerp(start[0], end[0] + 0.06, t);
  const z = lerp(start[2], end[2], t);
  const arc = Math.sin(t * Math.PI) * 0.22;
  const y = lerp(start[1] + 0.06, end[1] + 0.12, t) + arc;
  const tilt = Math.max(0, (progress - 0.5) / 0.5) * 1.1;
  const remainingFrac = Math.max(0, 1 - (progress - 0.5) / 0.5);
  const showStream = progress > 0.55 && progress < 0.95;
  const liquidColor = '#e8f4ff'; // clear/colourless for both

  return (
    <group>
      {/* Small reagent bottle held at an angle */}
      <group position={[x, y, z]} rotation={[0, 0, -tilt]}>
        <mesh castShadow>
          <cylinderGeometry args={[0.02, 0.022, 0.06, 18]} />
          <meshPhysicalMaterial color="#e7f1fb" transparent opacity={0.55} transmission={0.85} roughness={0.1} />
        </mesh>
        {/* Liquid inside, drains as it tilts */}
        <mesh position={[0, 0.005 - 0.018 * (1 - remainingFrac), 0]}>
          <cylinderGeometry args={[0.018, 0.018, 0.04 * remainingFrac + 0.005, 14]} />
          <meshPhysicalMaterial color={liquidColor} transparent opacity={0.85} transmission={0.75} ior={1.34} roughness={0.05} />
        </mesh>
        {/* Neck */}
        <mesh position={[0, 0.04, 0]}>
          <cylinderGeometry args={[0.008, 0.011, 0.02, 12]} />
          <meshPhysicalMaterial color="#dbe6f4" transparent opacity={0.55} transmission={0.7} roughness={0.15} />
        </mesh>
        {/* Reagent label */}
        <mesh position={[0, 0, 0.0235]}>
          <planeGeometry args={[0.028, 0.035]} />
          <meshStandardMaterial color="#fff7d8" roughness={0.85} />
        </mesh>
      </group>
      {/* Liquid stream */}
      {showStream && (
        <mesh position={[x - 0.04, (y + end[1]) / 2 - 0.02, z]} rotation={[0, 0, 0.08]}>
          <cylinderGeometry args={[0.0035, 0.009, Math.abs(y - end[1]) + 0.18, 12]} />
          <meshPhysicalMaterial color={liquidColor} transparent opacity={0.82} transmission={0.85} ior={1.34} roughness={0.05} depthWrite={false} />
        </mesh>
      )}
    </group>
  );
}

// =====================================================
// Part C — pour KI from tube 2 into tube 1
// =====================================================
export function usePartCKITubeTransform(
  isPouring: boolean,
  pourProgress: number,
  mixedTubeContents: 'empty' | 'pb' | 'mixed',
): { position: [number, number, number]; rotation: [number, number, number]; showStream: boolean } {
  const REST: [number, number, number] = [0.45, 0.0, 0];
  const HOVER: [number, number, number] = [-0.35, 0.30, 0]; // hover above Pb tube
  if (isPouring) {
    const moveT = easeInOut(Math.min(1, pourProgress / 0.55));
    const tiltT = easeInOut(Math.max(0, (pourProgress - 0.30) / 0.45));
    return {
      position: [
        lerp(REST[0], HOVER[0], moveT),
        lerp(REST[1], HOVER[1], moveT),
        lerp(REST[2], HOVER[2], moveT),
      ],
      rotation: [0, 0, -tiltT * 1.2],
      showStream: pourProgress > 0.45 && pourProgress < 0.85,
    };
  }
  if (mixedTubeContents === 'mixed') {
    // After the pour, the KI tube returns to its rest position (now empty)
    return { position: REST, rotation: [0, 0, 0], showStream: false };
  }
  return { position: REST, rotation: [0, 0, 0], showStream: false };
}

// =====================================================
// Part C — shake the mixed tube (small back-and-forth)
// =====================================================
export function usePartCShakeOffset(isShaking: boolean, shakeProgress: number): [number, number, number] {
  if (!isShaking) return [0, 0, 0];
  const amp = (1 - shakeProgress) * 0.025; // amplitude decays
  const wobble = Math.sin(shakeProgress * Math.PI * 14) * amp;
  return [wobble, 0, 0];
}
